import * as React from 'react';
import $ from 'jquery';
import '../css/popup.sass';
import StaticElementButton from './StaticElementButton';
import LinkButton from './LinkButton';

interface PopupProps {
  modal?: boolean;
  position?:
    | 'screen-center'
    | 'center'
    | 'relative'
    | 'table-action'
    | 'top-right';
  children?: React.ReactNode;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  className?: string;
  width?: string;
  theme?: 'night' | 'day';
  displayCloseButton?: boolean;
  onClose?: any;
  takeover?: boolean;
  animation?: 'slide-down'; // To animate display of the popup

  // If enabled, the popup will ONLY control the next element to be focused
  // if not doing so would result in focus lealving the popup
  alternateTabbingBehavior?: boolean;
  displayCancelLink?: boolean;
  cancelLinkLbl?: string;
}

interface PopupState {
  visible: boolean;
}

export default function Popup(props: PopupProps) {
  const [state, setState] = React.useState<PopupState>({ visible: true });

  const { position = 'center', cancelLinkLbl = 'Cancel' } = props;

  const popup: any = React.useRef();
  const popupContainer: any = React.useRef();

  const handleClickOutside = (event: any) => {
    if (popup && popup.current && popup.current.contains(event.target)) {
      return;
    }
    if (props.modal) {
      if (event.preventDefault !== undefined) {
        event.preventDefault();
      }
      return;
    }

    setState({ visible: false });
    if (props.onClose !== undefined) {
      props.onClose();
    }
  };

  const handleKeyDown = (e: any) => {
    if (
      e.keyCode === 27 &&
      !$(popup.current).find('.popup').length &&
      !e.popupClosed
    ) {
      setState({ visible: false });
      e.popupClosed = true;
      e.preventDefault();
      e.stopPropagation();
      if (props.onClose !== undefined) {
        props.onClose();
      }
    } else if (
      props.alternateTabbingBehavior &&
      e.key === 'Tab' &&
      !$(popup.current).find('.popup').length &&
      !e.popupClosed
    ) {
      const focusable: any[] = Array.from(
        popup.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element: any) => !element.disabled);
      if (e.shiftKey && document.activeElement === focusable[0]) {
        e.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (
        !e.shiftKey &&
        document.activeElement === focusable[focusable.length - 1]
      ) {
        e.preventDefault();
        focusable[0].focus();
      }
    }
  };

  const addTabbing = () => {
    const focusable = popup.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusable[0];
    if (firstFocusable) {
      const focusedItem = popup.current.querySelectorAll(
        'input[type="text"], input[type="password"], input[type="search"], select, textarea, .input-field'
      );
      if (focusedItem && focusedItem.length > 0) {
        const inputField = focusedItem[0].getElementsByTagName('input');
        if (inputField && inputField[0]) {
          inputField[0].focus();
        } else focusedItem[0].focus();
      } else {
        firstFocusable.focus();
      }

      const isDisable = (element: any) => {
        if (!element) {
          return true;
        }
        if (element.offsetLeft === 0) {
          // not visible
          return true;
        }
        const disableAttr = element.getAttribute('disabled');
        return !(
          disableAttr === null ||
          disableAttr === undefined ||
          disableAttr === false
        );
      };

      const focusNextEnabled = (initIndex: number) => {
        let index = initIndex;
        while (index < focusable.length - 1 && isDisable(focusable[index])) {
          index += 1;
        }
        if (index >= focusable.length || isDisable(focusable[index])) {
          index = 0;
          while (index < initIndex && isDisable(focusable[index])) {
            index += 1;
          }
          if (!isDisable(focusable[index])) {
            focusable[index].focus();
          }
        } else {
          focusable[index].focus();
        }
      };

      const focusPreEnabled = (initIndex: number) => {
        let index = initIndex;
        while (index > 0 && isDisable(focusable[index])) {
          index -= 1;
        }
        if (index < 0 || isDisable(focusable[index])) {
          index = focusable.length - 1;
          while (index > initIndex && isDisable(focusable[index])) {
            index -= 1;
          }
          if (!isDisable(focusable[index])) {
            focusable[index].focus();
          }
        } else {
          focusable[index].focus();
        }
      };

      focusable.forEach((node: any, index: number) => {
        const focusableNode = node;
        if (focusableNode) {
          focusableNode.onkeydown = (event: any) => {
            if (event.key === 'Tab') {
              if (event.shiftKey) {
                event.preventDefault();
                focusPreEnabled(index - 1);
              } else {
                event.preventDefault();
                focusNextEnabled(index + 1);
              }
            }
          };
        }
      });

      document.onkeydown = (event: any) => {
        if (popup.current && event.key === 'Tab') {
          let focusableActive = false;
          focusable.forEach((node: any) => {
            if (node === document.activeElement) {
              focusableActive = true;
            }
          });
          if (!focusableActive) {
            event.preventDefault();
            firstFocusable.focus();
          }
        }
      };
    }
  };

  let checkTabbingCounter = 0;
  const checkTabbing = () => {
    checkTabbingCounter += 1;
    if (popup && popup.current) {
      const focusable = popup.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusable[0];
      if (firstFocusable) {
        if (
          focusable.length === 1 &&
          focusable.item(0).id === 'closeButton' &&
          checkTabbingCounter < 4
        ) {
          setTimeout(checkTabbing, 500);
        } else {
          addTabbing();
        }
      }
    }
  };

  React.useEffect(() => {
    if (popupContainer.current) {
      $(popupContainer.current).offset(
        $(popupContainer.current).parents('.rap').offset()
      );
      $(popupContainer.current).width(
        $(popupContainer.current).parents('.rap').outerWidth(true)
      );
      $(popupContainer.current).height(
        $(popupContainer.current).parents('.rap').outerHeight()
      );
    }
    if (!props.alternateTabbingBehavior && popup.current) {
      checkTabbing();
    }
  }, []);

  const adjustPopupTopMargin = () => {
    const elm: any = document.getElementsByClassName('top-right')[0];
    const navElm: any = document.getElementsByTagName('nav')[0];
    const jumpNav: any = document.getElementsByClassName('jump-nav')[0];
    if (elm) {
      const additionalMargin = jumpNav ? 32 : 0;
      elm.style.marginTop = `${80 + additionalMargin}px`;
      if (navElm) {
        const navPos = navElm.getBoundingClientRect().top;
        const totalMargin = navPos < 0 ? 24 : 80;
        elm.style.marginTop = `${totalMargin + additionalMargin}px`;
      }
    }
  };

  const handleScroll = () => {
    adjustPopupTopMargin();
  };

  React.useEffect(() => {
    if (
      popup.current &&
      popup.current.firstElementChild &&
      props.position === 'table-action'
    ) {
      popup.current.style.marginLeft = `-${
        popup.current.firstElementChild.offsetWidth - 50
      }px`;
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [props]);

  React.useEffect(() => {
    if (props.takeover && state.visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  }, [props.takeover, state.visible]);

  React.useEffect(() => {
    if (props.position === 'top-right') {
      adjustPopupTopMargin();
    }
  }, [props.position]);

  const popupComponentInner = (
    <div
      ref={popup}
      role="alertdialog"
      aria-labelledby={props.ariaLabelledby}
      aria-describedby={props.ariaDescribedby}
      aria-modal="true"
      className={`popup ${position} ${props.theme || ''} ${
        props.className || ''
      }${props.takeover ? ' takeover' : ''} ${
        props.animation ? props.animation : ''
      }`}
      style={props.width ? { width: props.width } : {}}
    >
      {props.displayCloseButton && (
        <StaticElementButton
          name="closeButton"
          id="closeButton"
          label="Close Popup"
          type="div"
          onClick={() => {
            setState({ visible: false });
            if (props.onClose !== undefined) {
              props.onClose();
            }
          }}
          className="close-button"
        />
      )}
      {props.displayCancelLink && (
        <div className="cancel-link">
          <LinkButton
            name="cancelLink"
            id="cancelLink"
            smol
            onClick={() => {
              setState({ visible: false });
              if (props.onClose !== undefined) {
                props.onClose();
              }
            }}
          >
            {cancelLinkLbl}
          </LinkButton>
        </div>
      )}
      {props.takeover ? (
        <div className="container">{props.children}</div>
      ) : (
        props.children
      )}
    </div>
  );

  const popupComponent =
    props.position === 'table-action' ? (
      <div className="popupParentBox">{popupComponentInner}</div>
    ) : (
      popupComponentInner
    );

  return state.visible ? (
    <>
      <div className="popupParentWindow">&nbsp;</div>
      {props.position === 'center' || props.position === 'screen-center' ? (
        <div
          className={`popupContainer ${props.position}${
            props.takeover ? ' takeover' : ''
          }`}
          ref={popupContainer}
        >
          {popupComponent}
        </div>
      ) : (
        popupComponent
      )}
    </>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
}
